import Paper from '@/components/Paper'
import { useState, useMemo, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { SalesTargetFilters } from '../../../../BusinessTargets/types'
import PerformanceCard from './PerformanceCard'
import FilterControls from './FilterControls'
import PaginationControls from './PaginationControls'
import { EmployeePerformance, FilterType, PeriodType } from './types'
import { cn } from '@/lib/utils'
import { Typogaphy } from 'vnetwork-ui'
import { useQueryWithHandler } from '@/hooks/useQueryWithHandler'
import { QUERY_KEYS } from '@/constants/reactQueryKeys'
import {
    getEmployeeDashboard,
    getEmployeesByIds,
    getActivityTargets,
    getEmployeeActivityActuals,
} from '@/modules/Report/services'
import usePermissionsStore from '@/stores/usePermissionsStore'
import useAuthStore from '@/stores/useAuthStore'

interface SalesPerformanceProps {
    filters: SalesTargetFilters
}

const determineStatus = (
    achievementPercentage: number,
    metrics: {
        target: number
        actual: number
    }[]
): 'achieved' | 'not_achieved' => {
    const allMetricsAchieved =
        metrics.length == 0 ||
        metrics.every((metric) => metric.actual >= metric.target)

    if (achievementPercentage >= 100 && allMetricsAchieved) return 'achieved'
    return 'not_achieved'
}

export default function SalesPerformance({ filters }: SalesPerformanceProps) {
    const { t } = useTranslation()
    const { isAdmin } = usePermissionsStore()
    const { employee } = useAuthStore()

    // State management
    const [_viewType, _setViewType] = useState<'card' | 'table'>('card')
    const [filterType, setFilterType] = useState<FilterType>('all')
    const [period, setPeriod] = useState<PeriodType>('month')
    const [currentPage, setCurrentPage] = useState(1)
    const [pageSize, setPageSize] = useState(10)

    // Fetch real data from API
    const { data: dashboardData, renderState: renderDashboardState } =
        useQueryWithHandler({
            queryKey: [QUERY_KEYS.EMPLOYEE_DASHBOARD, filters],
            queryFn: () =>
                getEmployeeDashboard(
                    filters,
                    !isAdmin ? employee?.employee_id : undefined
                ),
            enabled:
                !!filters.employee_id &&
                filters.employee_id.length > 0 &&
                !!filters.gte &&
                !!filters.lte,
        })

    // Fetch employee details (for department names)
    const { data: employeeDetails, renderState: renderEmployeesState } =
        useQueryWithHandler({
            queryKey: [QUERY_KEYS.EMPLOYEES, 'contains', filters.employee_id],
            queryFn: () => getEmployeesByIds(filters.employee_id!),
            enabled: !!filters.employee_id && filters.employee_id.length > 0,
        })

    // Fetch activity targets
    const { data: activityTargetsData, renderState: renderTargetsState } =
        useQueryWithHandler({
            queryKey: [QUERY_KEYS.ACTIVITY_TARGETS, filters],
            queryFn: () =>
                getActivityTargets(
                    filters,
                    !isAdmin ? employee?.employee_id : undefined
                ),
            enabled:
                !!filters.employee_id &&
                filters.employee_id.length > 0 &&
                !!filters.gte &&
                !!filters.lte,
        })

    // Fetch activity actuals
    const { data: activityActualsData, renderState: renderActualsState } =
        useQueryWithHandler({
            queryKey: [QUERY_KEYS.ACTIVITY_ACTUALS, filters],
            queryFn: () =>
                getEmployeeActivityActuals({
                    gte: filters.gte || '',
                    lte: filters.lte || '',
                    sales_manager_codes: filters.employee_id,
                }),
            enabled:
                !!filters.employee_id &&
                filters.employee_id.length > 0 &&
                !!filters.gte &&
                !!filters.lte,
        })

    const stateComponent =
        renderDashboardState() ||
        renderEmployeesState() ||
        renderTargetsState() ||
        renderActualsState()

    // Create a map of employee_id -> department_name
    const departmentMap = useMemo(() => {
        if (!employeeDetails) return new Map<number, string>()
        return new Map(
            employeeDetails.map((emp) => [
                emp.employee_id,
                emp.department_name || '',
            ])
        )
    }, [employeeDetails])

    // Aggregate activity targets by employee_id
    const activityTargetsMap = useMemo(() => {
        const map = new Map<
            number,
            { presentation: number; poc: number; contract: number }
        >()

        if (!activityTargetsData) return map

        activityTargetsData.forEach((target) => {
            const empId = Number(target.employee_id)
            const current = map.get(empId) || {
                presentation: 0,
                poc: 0,
                contract: 0,
            }

            map.set(empId, {
                presentation:
                    current.presentation + (target.target_presentation || 0),
                poc: current.poc + (target.target_poc || 0),
                contract: current.contract + (target.target_contract || 0),
            })
        })

        return map
    }, [activityTargetsData])

    // Aggregate activity actuals by employee_id
    const activityActualsMap = useMemo(() => {
        const map = new Map<
            number,
            { presentation: number; poc: number; contract: number }
        >()

        if (!activityActualsData) return map

        activityActualsData.forEach((actual) => {
            map.set(actual.employee_id, {
                presentation: actual.presentation,
                poc: actual.poc,
                contract: actual.contract,
            })
        })

        return map
    }, [activityActualsData])

    // Transform API data to EmployeePerformance
    const allEmployees: EmployeePerformance[] = useMemo(() => {
        const dashboardMap = new Map(
            dashboardData?.employees?.map((emp) => [emp.employee_id, emp]) || []
        )

        // Determine the source list of employees to display
        // If filters are applied, use employeeDetails to ensure all selected employees are shown
        // Otherwise, use the dashboardData list
        let sourceList: { employee_id: number; employee_name: string }[] = []

        if (
            filters.employee_id &&
            filters.employee_id.length > 0 &&
            employeeDetails
        ) {
            sourceList = employeeDetails
        } else if (dashboardData?.employees) {
            sourceList = dashboardData.employees
        }

        return sourceList.map((emp) => {
            const dashboardEmp = dashboardMap.get(Number(emp.employee_id))

            const activityTargets = activityTargetsMap.get(
                Number(emp.employee_id)
            ) || {
                presentation: 0,
                poc: 0,
                contract: 0,
            }

            const activityActuals = activityActualsMap.get(
                Number(emp.employee_id)
            ) || {
                presentation: 0,
                poc: 0,
                contract: 0,
            }

            return {
                employee_id: emp.employee_id,
                employee_name: emp.employee_name,
                department: departmentMap.get(emp.employee_id) || '',
                target_amount: dashboardEmp?.target_amount || 0,
                actual_revenue: dashboardEmp?.actual_revenue || 0,
                renewal_revenue: dashboardEmp?.renewal_revenue || 0,
                new_sale_revenue: dashboardEmp?.new_sale_revenue || 0,
                upsell_revenue: dashboardEmp?.upsell_revenue || 0,
                achievement_percentage:
                    dashboardEmp?.achievement_percentage || 0,
                allocation_count: dashboardEmp?.allocation_count || 0,
                status: determineStatus(
                    dashboardEmp?.achievement_percentage || 0,
                    [
                        {
                            actual: activityActuals.presentation,
                            target: activityTargets.presentation,
                        },
                        {
                            actual: activityActuals.poc,
                            target: activityTargets.poc,
                        },
                        {
                            actual: activityActuals.contract,
                            target: activityTargets.contract,
                        },
                    ]
                ),
                metrics: {
                    presentation: {
                        actual: activityActuals.presentation,
                        target: activityTargets.presentation,
                    },
                    poc: {
                        actual: activityActuals.poc,
                        target: activityTargets.poc,
                    },
                    contract: {
                        actual: activityActuals.contract,
                        target: activityTargets.contract,
                    },
                },
            }
        })
    }, [
        dashboardData,
        employeeDetails,
        filters.employee_id,
        departmentMap,
        activityTargetsMap,
        activityActualsMap,
    ])

    // Apply filters
    const filteredEmployees = useMemo(() => {
        let filtered = [...allEmployees]

        // Apply status filter
        if (filterType === 'achieved') {
            filtered = filtered.filter((emp) => emp.status === 'achieved')
        } else if (filterType === 'not_achieved') {
            filtered = filtered.filter((emp) => emp.status === 'not_achieved')
        }

        return filtered
    }, [allEmployees, filterType])

    // Apply pagination
    const paginatedEmployees = useMemo(() => {
        const start = (currentPage - 1) * pageSize
        const end = start + pageSize
        return filteredEmployees.slice(start, end)
    }, [filteredEmployees, currentPage, pageSize])

    const totalPages = Math.ceil(filteredEmployees.length / pageSize)

    // Reset page when filter changes
    useEffect(() => {
        setCurrentPage(1)
    }, [filterType])

    // Handle page size change
    const handlePageSizeChange = (newSize: number) => {
        setPageSize(newSize)
        setCurrentPage(1)
    }

    const handleEmployeeClick = (employee: EmployeePerformance) => {
        console.log('Employee clicked:', employee)
        // TODO: Navigate to employee detail page or show modal
    }

    const hasData = allEmployees.length > 0

    return (
        <Paper className="h-full flex flex-col gap-4">
            {/* Header */}
            <div className="flex items-center justify-between">
                <Typogaphy className="text-sm font-medium text-[#1A237E]">
                    {t('sales_performance') || 'Hiệu suất bán hàng'}
                </Typogaphy>

                {/* Filter Controls */}
                {isAdmin && (
                    <FilterControls
                        filter={filterType}
                        period={period}
                        onFilterChange={setFilterType}
                        onPeriodChange={setPeriod}
                    />
                )}
            </div>

            {/* Loading/Error State */}
            {stateComponent}

            {/* Empty State */}
            {!stateComponent && !hasData && (
                <div className="text-center py-12">
                    <p className="text-xs text-gray-400 italic">
                        Vui lòng chọn phòng ban và nhân viên để xem dữ liệu
                    </p>
                </div>
            )}

            {/* Content Area - Cards or Table */}
            {!stateComponent && hasData && (
                <>
                    {/* Performance Cards Grid */}
                    <div
                        className={cn(
                            'grid gap-4 transition-opacity duration-300',
                            'grid-cols-1 md:grid-cols-2',
                            paginatedEmployees.length < 2 && '!grid-cols-1'
                        )}
                    >
                        {paginatedEmployees.map((employee) => (
                            <PerformanceCard
                                key={employee.employee_id}
                                employee={employee}
                                onEmployeeClick={handleEmployeeClick}
                            />
                        ))}
                    </div>

                    {/* Empty State for filtered */}
                    {paginatedEmployees.length === 0 && (
                        <div className="text-center py-12">
                            <p className="text-xs text-gray-400 italic">
                                {t('no_data_available') || 'Không có dữ liệu'}
                            </p>
                        </div>
                    )}

                    {/* Pagination */}
                    {filteredEmployees.length > 0 && (
                        <PaginationControls
                            currentPage={currentPage}
                            totalPages={totalPages}
                            pageSize={pageSize}
                            totalRecords={filteredEmployees.length}
                            onPageChange={setCurrentPage}
                            onPageSizeChange={handlePageSizeChange}
                        />
                    )}
                </>
            )}
        </Paper>
    )
}
